

import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc, arrayUnion, increment } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import SuccessScreen from "./SuccessScreen";
import { createClients } from "../firebase/clients";

const INTEREST_KEYS = [
  { key: "music", label: "Music", icon: "🎵", color: "bg-gradient-to-r from-[#F6A655] to-[#F2800D]" },
  { key: "art", label: "Art", icon: "🎨", color: "bg-gradient-to-r from-[#CF86EA] to-[#9D54B8]" },
  { key: "fitness", label: "Fitness", icon: "💪", color: "bg-gradient-to-r from-[#67C5E4] to-[#3399CC]" },
  { key: "food", label: "Food", icon: "🍔", color: "bg-gradient-to-r from-[#EBBD60] to-[#F2A60D]" },
  { key: "travel", label: "Travel", icon: "✈️", color: "bg-gradient-to-r from-[#3366CC] to-[#24368F]" },
  { key: "sports", label: "Sports", icon: "⚽", color: "bg-gradient-to-r from-[#6DCA4E] to-[#48A329]" }
];

export default function CheckInPage({ user }) {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({ name: false, email: false });

  // 🔥 Install banner
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const clientsRef = useRef({});

  // -----------------------------
  // Init + cache + auth
  // -----------------------------
  useEffect(() => {
    if (!eventId) return;

    const { db, auth } = createClients();
    clientsRef.current = { db };

    const visitKey = `event-${eventId}-visited`;
    if (!localStorage.getItem(visitKey)) {
      setDoc(doc(db, "events", eventId), { total: increment(1) }, { merge: true })
        .then(() => localStorage.setItem(visitKey, "true"))
        .catch(console.error);
    }

    const cached = localStorage.getItem(`event-${eventId}-user`);
    if (cached) {
      const data = JSON.parse(cached);
      setName(data.name || "");
      setEmail(data.email || "");
      setInterests(data.interests || []);
      setSubmitted(!!data.submitted);
    }

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      const snap = await getDoc(doc(db, "users", u.uid));
      if (snap.exists()) {
        const d = snap.data();
        setName(d.name || "");
        setEmail(d.email || "");
        if (d.eventsAttended?.includes(eventId)) setSubmitted(true);
      }
    });

    return () => unsub();
  }, [eventId]);

  // -----------------------------
  // 🔥 Show install banner after 3s (once)
  // -----------------------------
  useEffect(() => {
    if (!eventId) return;

    const bannerKey = `install-banner-shown-${eventId}`;

    if (localStorage.getItem(bannerKey)) return;

    const t = setTimeout(() => {
      setShowInstallBanner(true);
      localStorage.setItem(bannerKey, "true");
    }, 3000);

    return () => clearTimeout(t);
  }, [eventId]);


  function toggleInterest(key) {
    setInterests((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : prev.length >= 2
        ? prev
        : [...prev, key]
    );
  }

  async function handleSubmit() {
    const newErrors = {
      name: !name.trim(),
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    };
    setErrors(newErrors);

    if (newErrors.name || newErrors.email || !interests.length) {
      alert("Please fill all fields and select at least one interest.");
      return;
    }

    setLoading(true);
    try {
      const { db } = clientsRef.current;

      await setDoc(
        doc(db, "users", user.uid),
        {
          name,
          email,
          createdAt: new Date().toISOString(),
          eventsAttended: arrayUnion(eventId),
        },
        { merge: true }
      );

      const updates = {};
      interests.forEach((i) => (updates[`interests.${i}`] = increment(1)));
      await setDoc(doc(db, "events", eventId), updates, { merge: true });

      localStorage.setItem(
        `event-${eventId}-user`,
        JSON.stringify({ name, email, interests, submitted: true })
      );

      setSubmitted(true);
    } catch {
      alert("Registration failed");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) return <SuccessScreen interests={interests} />;

  return (
    <>
      {/* MAIN PAGE */}
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-sm">
          <div className="text-center mb-3">
            <img src="/goBanterLogo_red.svg" alt="GoBanter" className="mx-auto h-16 mb-3" />
            <h2 className="text-3xl font-extrabold text-[#574F42]">Join the event!</h2>
            <p className="text-gray-500 mt-2">
              We won’t spam — just need your name and email.
            </p>
          </div>

          <input
            ref={nameInputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="w-full p-4 mb-4 border rounded-2xl"
          />

          <input
            ref={emailInputRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full p-4 mb-6 border rounded-2xl"
          />

          <div className="grid grid-cols-2 gap-3 mb-4">
            {INTEREST_KEYS.map((it) => (
              <button
                key={it.key}
                onClick={() => toggleInterest(it.key)}
                className={`p-4 rounded-full font-semibold ${
                  interests.includes(it.key) ? `${it.color} text-white` : "bg-gray-200"
                }`}
              >
                {it.icon} {it.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !interests.length}
            className="w-full py-4 rounded-full bg-black text-white font-bold"
          >
            {loading ? "Checking in..." : "Check In"}
          </button>
        </div>
      </div>

      {/* 🔥 INSTALL BANNER */}

     {showInstallBanner && (
       <div className="fixed inset-0 z-50 flex items-end">
         <div
           className="absolute inset-0 bg-black/40"
         />
         <div className="relative w-full bg-white rounded-t-3xl p-5 animate-slide-up text-center">
           <h3 className="text-xl font-extrabold mb-2">Install GoBanter App</h3>
           <p className="text-gray-500 mb-4">
             Get the full experience and stay connected.
           </p>

           <div className="flex gap-3 justify-center">
             <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">
               <img
                 src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                 alt="Download on the App Store"
                 className="h-12"
               />
             </a>

             <a href="https://play.google.com/store" target="_blank" rel="noopener noreferrer">
               <img
                 src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                 alt="Get it on Google Play"
                 className="h-12"
               />
             </a>
           </div>
         </div>
       </div>
     )}
     )}

    </>
  );
}
